import type { BBoxNorm } from "@/viewer/bbox";

export const ZOOM_LEVELS = [1, 2, 4, 8] as const;
export type Zoom = (typeof ZOOM_LEVELS)[number];

/**
 * The smallest zoom level at which a box reads: its longer rendered edge is at least
 * `minEdge` CSS pixels. A 1/8" tag on an ARCH D sheet fitted to a laptop is a few pixels
 * wide, so this usually lands on 4x or 8x; a whole section detail fits at 1x or 2x.
 */
export function zoomForBox(
  box: BBoxNorm,
  fitWidth: number,
  fitHeight: number,
  minEdge = 160,
  levels: readonly Zoom[] = ZOOM_LEVELS,
): Zoom {
  const edge = Math.max(
    Math.abs(box.x1 - box.x0) * fitWidth,
    Math.abs(box.y1 - box.y0) * fitHeight,
  );
  for (const level of levels) {
    if (edge * level >= minEdge) {
      return level;
    }
  }
  return levels[levels.length - 1] ?? 1;
}

/** Where to scroll a stage of `viewport` size so the box, rendered at `css` size, is centred. */
export function scrollToCentre(
  box: BBoxNorm,
  css: { width: number; height: number },
  viewport: { width: number; height: number },
): { left: number; top: number } {
  const cx = ((box.x0 + box.x1) / 2) * css.width;
  const cy = ((box.y0 + box.y1) / 2) * css.height;
  return {
    left: Math.max(0, cx - viewport.width / 2),
    top: Math.max(0, cy - viewport.height / 2),
  };
}

/** The next zoom level in or out, held at either end. */
export function stepZoom(zoom: Zoom, direction: 1 | -1): Zoom {
  const i = ZOOM_LEVELS.indexOf(zoom);
  return ZOOM_LEVELS[Math.min(ZOOM_LEVELS.length - 1, Math.max(0, i + direction))] ?? zoom;
}
